import { createClient } from "@supabase/supabase-js";
import axios from "axios";
import env from "../config/env";

const supabaseUrl = "https://ahiwuudijsiexpfjobpq.supabase.co";
const supabaseKey = env.supabase.key as string;

export const supabase = createClient(supabaseUrl, supabaseKey);

export const getUser = async () => {
  const { data: session } = await supabase.auth.getSession();
  // console.log(session);
  if (session !== null) {
    const { data: user, error } = await supabase
      .from("customer")
      .select("*")
      .eq("auth_id", session?.session?.user.id);
    if (user) {
      return user;
    }
  } else {
    return null;
  }
};

export const getCustomers = async () => {
  const { data, error } = await supabase.from("customer").select("*");
  if (data) {
    console.log(data);
    return data;
  } else {
    return null;
  }
};

export const getProduct = async (id: number) => {
  const { data, error } = await supabase
    .from("products")
    .select("*")
    .eq("id", id);
  if (data) {
    return data[0];
  } else {
    return null;
  }
};

export const getCustomPricing = async (auth_id: string, product_id: number) => {
  const { data, error } = await supabase
    .from("custom_pricing")
    .select("*")
    .eq("auth_id", auth_id)
    .eq("product_id", product_id);

  if (data) {
    return data[0];
  } else {
    return null;
  }
};

export const getCustomPricingActive = async (
  auth_id: string,
  product_id: number
) => {
  const { data, error } = await supabase
    .from("custom_pricing")
    .select("*")
    .eq("auth_id", auth_id)
    .eq("product_id", product_id)
    .eq("active", true);

  if (data && data.length > 0) {
    return data[0];
  } else {
    return null;
  }
};

// ============================================
// Backend API Functions for Custom Pricing
// ============================================

/**
 * Get custom pricing for a specific customer and product via backend API
 */
export const getCustomPricingAPI = async (
  auth_id: string,
  product_id: number
) => {
  const headers = {
    "Content-Type": "application/json",
  };
  try {
    const res = await axios.get(
      `${env.backendUrl}/api/custom-pricing/${auth_id}/${product_id}`,
      { headers }
    );
    return res.data.custom_pricing;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null; // No custom pricing found
    }
    console.error("Error getting custom pricing:", error);
    throw error;
  }
};

/**
 * Get all custom pricing for a customer via backend API
 */
export const getCustomerPricingAPI = async (auth_id: string) => {
  const headers = {
    "Content-Type": "application/json",
  };
  try {
    const res = await axios.get(
      `${env.backendUrl}/api/custom-pricing/customer/${auth_id}`,
      { headers }
    );
    return res.data.custom_pricing;
  } catch (error: any) {
    console.error("Error getting customer pricing:", error);
    throw error;
  }
};

/**
 * Create or update custom pricing via backend API
 */
export const upsertCustomPricingAPI = async (params: {
  auth_id: string;
  product_id: number;
  price: number;
  active: boolean;
}) => {
  const headers = {
    "Content-Type": "application/json",
  };
  try {
    const res = await axios.post(
      `${env.backendUrl}/api/custom-pricing`,
      params,
      { headers }
    );
    return res.data;
  } catch (error: any) {
    console.error("Error upserting custom pricing:", error);
    throw error;
  }
};

/**
 * Delete custom pricing via backend API
 */
export const deleteCustomPricingAPI = async (id: string) => {
  const headers = {
    "Content-Type": "application/json",
  };
  try {
    const res = await axios.delete(
      `${env.backendUrl}/api/custom-pricing/${id}`,
      { headers }
    );
    return res.data;
  } catch (error: any) {
    console.error("Error deleting custom pricing:", error);
    throw error;
  }
};

export const getProducts = async () => {
  const { data, error } = await supabase.from("products").select("*");
  if (data) {
    return data;
  } else {
    return null;
  }
};

export const getOrders = async () => {
  const { data, error } = await supabase.from("document_orders").select("*");
  if (data) {
    return data;
  } else {
    return null;
  }
};

export const getOrderDetails = async () => {
  const { data, error } = await supabase
    .from("order_details")
    .select("*")
    .order("created_at", { ascending: false });
  if (data) {
    return data;
  } else {
    return null;
  }
};

export const getUserOrders = async (userId: string) => {
  const { data, error } = await supabase
    .from("order_details")
    .select("*")
    .eq("auth_id", userId)
    .order("created_at", { ascending: false });
  if (data) {
    return data;
  } else {
    console.log(error);
    return null;
  }
};

export const invokeFunction = async (functionName: string, params: any) => {
  const url = `https://ahiwuudijsiexpfjobpq.supabase.co/functions/v1/${functionName}`;

  const headers = {
    Authorization: `Bearer ${supabaseKey}`,
    "Content-Type": "application/json",
  };

  var config = {
    method: "post",
    url: url,
    headers: headers,
    data: params,
    crossdomain: true,
  };

  let d = await axios(config);

  return d.data;
};

export const processDocument = async (endpoint: string, params: any) => {
  const headers = {
    "Allow-Control-Allow-Origin": "*",
  };
  const res = await axios.post(
    `${env.backendUrl}/api/documents/${endpoint}`,
    params,
    { headers }
  );
  return res.data;
};

/**
 * Fetches a pre-made document from the backend (interim solution)
 * The backend will return the PDF from Supabase storage based on document type
 */
export const getPreMadeDocument = async (
  document_type: string,
  auth_id: string,
  order_id: string
) => {
  try {
    console.log("🔄 [GET-DOCUMENT] Request:", {
      endpoint: `${env.backendUrl}/api/documents/get-document`,
      document_type,
      auth_id: auth_id.substring(0, 8) + "...",
      order_id,
    });

    // Note: Don't set CORS headers from client - they're set by the server
    const res = await axios.post(
      `${env.backendUrl}/api/documents/get-document`,
      {
        document_type,
        auth_id,
        order_id,
      },
      {
        responseType: "blob", // Important for receiving PDF
      }
    );

    console.log("✅ [GET-DOCUMENT] Success:", {
      status: res.status,
      contentType: res.headers["content-type"],
      size: res.data?.size || "unknown",
    });

    return res.data;
  } catch (error: any) {
    console.error("❌ [GET-DOCUMENT] Failed:", {
      message: error?.message,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      data: error?.response?.data,
      url: `${env.backendUrl}/api/documents/get-document`,
      document_type,
    });
    throw error;
  }
};

// MIGRATED TO BACKEND - See new implementation below in "NEW BACKEND API FUNCTIONS" section

export const submitTrustDeedForm = async (params: any) => {
  const functionName = "submit_trust_deed_form";
  return await invokeFunction(functionName, params);
};

// ============================================
// NEW BACKEND API FUNCTIONS (Migrated from Supabase)
// ============================================

/**
 * Stripe Functions - now calling backend instead of Supabase
 */

export const createPaymentIntent = async (params: {
  price: number;
  description: string;
  createCustomer: boolean;
  email: string;
  name: string;
  auth_id: string;
  dev?: boolean;
}) => {
  const headers = {
    "Content-Type": "application/json",
  };
  const res = await axios.post(
    `${env.backendUrl}/api/stripe/create-payment-intent`,
    params,
    { headers }
  );
  return res.data;
};

export const getInvoiceFromPaymentIntent = async (
  payment_intent: string,
  auth_id: string,
  order_id: string
) => {
  const headers = {
    "Content-Type": "application/json",
  };
  const res = await axios.post(
    `${env.backendUrl}/api/stripe/get-invoice`,
    { payment_intent, auth_id, order_id },
    { headers }
  );
  return res.data;
};

export const getCustomerPaymentMethods = async (params: {
  stripe_id?: string;
  email?: string;
  dev?: boolean;
}) => {
  const headers = {
    "Content-Type": "application/json",
  };
  const res = await axios.post(
    `${env.backendUrl}/api/stripe/get-payment-methods`,
    params,
    { headers }
  );
  return res.data;
};

export const processPayment = async (params: {
  amount: number;
  currency?: string;
  customer: string;
  payment_method: string;
  description?: string;
  dev?: boolean;
}) => {
  const headers = {
    "Content-Type": "application/json",
  };
  const res = await axios.post(
    `${env.backendUrl}/api/stripe/process-payment`,
    params,
    { headers }
  );
  return res.data;
};

/**
 * Email Functions - now calling backend instead of Supabase
 */

export const sendContactEmailBackend = async (params: {
  email: string;
  name: string;
  message: string;
  subject?: string;
}) => {
  const headers = {
    "Content-Type": "application/json",
  };
  const res = await axios.post(
    `${env.backendUrl}/api/email/send-contact`,
    params,
    { headers }
  );
  return res.data;
};

export const sendDocumentsEmail = async (params: {
  orderId: string;
  email: string;
  invoice?: boolean;
  document?: boolean;
  instructions?: boolean;
}) => {
  const headers = {
    "Content-Type": "application/json",
  };
  const res = await axios.post(
    `${env.backendUrl}/api/email/send-documents`,
    params,
    { headers }
  );
  return res.data;
};
